/*
 *   Copyright (c) 2025 2023 Celadon Development LLC
 *   All rights reserved.
 *   Author Aliaksandr Ziabko <aliaksandr.ziabko@celadonsoft.com>
 */
import React, { useEffect, useState, useCallback } from "react";
import Header from "../../components/Header";
import AddButton from "../../components/AddButton";
import DeleteModal from "../../components/DeleteModal";
import Pagination from "../../components/Pagination";
import Loading from "../../components/Loading";
import SuccessMessage from "../../components/SuccessMessage";
import ErrorMessage from "../../components/ErrorMessage";

import { PencilIcon, TrashIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router";

import {
  getHowToPerforms,
  deleteHowToPerform,
  HowToPerform,
  searchHTP,
} from "../../services/howToPerformService";
import Select from "react-select";
import { debounce } from "lodash";

interface FilterOption {
  label: string;
  value: string;
}

const HowToPerformPage: React.FC = () => {
  const navigate = useNavigate();

  const [data, setData] = useState<HowToPerform[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [isLoadingNames, setIsLoadingNames] = useState(false);
  const [nameOptions, setNameOptions] = useState<FilterOption[]>([]);

  const [categoryOptions, setCategoryOptions] = useState<FilterOption[]>([]);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<HowToPerform | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedNames, setSelectedNames] = useState<FilterOption[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<FilterOption[]>(
    []
  );

  const debouncedHtpNameSearch = debounce(async (inputValue: string) => {
    setIsLoadingNames(true);
    try {
      const results = await searchHTP("name", inputValue);
      // results = [{ id, name }]
      setNameOptions(
        results.map((htp) => ({ label: htp.name, value: htp.name }))
      );
    } catch (error) {
      console.error("Error searching HTP names:", error);
    } finally {
      setIsLoadingNames(false);
    }
  }, 300);

  const debouncedHtpCategorySearch = debounce(async (inputValue: string) => {
    console.log("Category input:", inputValue);
    if (inputValue) {
      try {
        const results = await searchHTP("categoryName", inputValue);

        const options = results.map((c) => ({
          label: c.name ? String(c.name) : "",
          value: c.id != null ? String(c.id) : "",
        }));

        console.log("Category options:", options);
        setCategoryOptions(options);
      } catch (error) {
        console.error("Error searching HTP categories:", error);
      }
    } else {
      setCategoryOptions([]);
    }
  }, 500);

  /* =========================
     FETCH DATA
  ========================= */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const filters = {
        page: currentPage,
        limit: 10,
        name: selectedNames.map((option) => option.value),
        categoriesIds: selectedCategories.map((option) => option.value),
      };

      const data = await getHowToPerforms(filters);
      setData(data.data);

      setTotalPages(data.totalPages);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch Trainer Tutorials list");
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedNames, selectedCategories]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  console.log(data);

  /* =========================
     DELETE
  ========================= */
  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      await deleteHowToPerform(itemToDelete.id);
      setSuccessMessage("Tutorial deleted successfully");
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch (err) {
      console.error(err);
      setError("Failed to delete Tutorial");
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <Header title="Trainer Tutorials" />
      <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Select
          isMulti
          options={nameOptions}
          value={selectedNames}
          onChange={(selected) => setSelectedNames(selected as FilterOption[])}
          onInputChange={(input) => {
            if (typeof input === "string") {
              return debouncedHtpNameSearch(input);
            }
          }}
          placeholder="Filter by Name"
          className="basic-multi-select"
          classNamePrefix="select"
          isLoading={isLoadingNames}
          isClearable
        />

        <Select
          isMulti
          options={categoryOptions}
          value={selectedCategories}
          onChange={(selected) => {
            setSelectedCategories(selected as FilterOption[]);
          }}
          onInputChange={(input) => {
            if (typeof input === "string") {
              return debouncedHtpCategorySearch(input);
            }
          }}
          placeholder="Filter by Category"
          className="basic-multi-select"
          classNamePrefix="select"
          isClearable
        />
      </div>
      <div className="my-4 flex justify-end">
        <AddButton text="Add Tutorial" onClick={() => navigate("/add-htp")} />
      </div>

      {loading ? (
        <Loading message="Loading form..." />
      ) : (
        <>
          {successMessage && (
            <SuccessMessage
              message={successMessage}
              onClose={() => setSuccessMessage(null)}
            />
          )}
          {error && (
            <ErrorMessage message={error} onClose={() => setError(null)} />
          )}

          <div className="rounded-lg overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="bg-gray-50">
                  <th className="py-2 px-4 text-left">ID</th>
                  <th className="py-2 px-4 text-left">Name</th>
                  <th className="py-2 px-4 text-left">Categories</th>
                  <th className="py-2 px-4 text-left">Image</th>
                  <th className="py-2 px-4 text-left">Created</th>
                  <th className="py-2 px-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item) => (
                  <tr key={item.id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4">{item.id}</td>

                    <td className="py-3 px-4 font-medium">{item.name}</td>

                    <td className="py-3 px-4">
                      {item.categories?.map((c) => c.name).join(", ") || "-"}
                    </td>

                    <td className="py-3 px-4">
                      {item.imageUrls && item.imageUrls[0] && (
                        <img
                          src={item.imageUrls[0]}
                          alt="Preview"
                          className="w-14 h-14 object-cover border rounded"
                        />
                      )}
                    </td>

                    <td className="py-3 px-4 text-sm text-gray-500">
                      {new Date(item.createdAt).toLocaleDateString()}
                    </td>

                    <td className="py-3 px-4 flex gap-2">
                      <button
                        onClick={() => navigate("/edit-htp", { state: item })}
                        className="px-2 py-1 text-yellow-600 border border-yellow-600 rounded hover:bg-gray-100"
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>

                      <button
                        onClick={() => {
                          setItemToDelete(item);
                          setIsDeleteModalOpen(true);
                        }}
                        className="px-2 py-1 text-red-600 border border-red-600 rounded hover:bg-gray-100"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemName={itemToDelete?.name || ""}
        itemType="HTP"
      />
    </div>
  );
};

export default HowToPerformPage;
